import { useEffect, useState } from 'react';
import { AiOutlineCheck, AiOutlineClose } from 'react-icons/ai';
import { BsEnvelope, BsKey, BsPerson } from 'react-icons/bs';
import { validateEmail } from '../../core/utils/string';
import { DefaultButton } from '../Button/Button';
import { LabelInput, SelectInput } from '../Input/Input';
import './ModalAddUser.scss';

const USER_TYPES = [
  { value: 1, label: 'Administrador' },
  { value: 2, label: 'Gestor' },
  { value: 3, label: 'Membro' },
];

const ModalAddUser = ({
  isOpen,
  onClose,
  onSave,
  existingEmails = [],
  editUser = null,
  isLoading = false,
}) => {
  const isEditMode = !!editUser;

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    type: null,
    name: '',
  });

  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Limpa o formulário quando o modal fecha
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        type: null,
        name: '',
      });
      setErrors({});
    } else if (editUser) {
      // Preenche o formulário com dados do usuário em edição
      setFormData({
        email: editUser.email || '',
        password: '',
        confirmPassword: '',
        type: USER_TYPES.find((t) => t.value === editUser.type) || null,
        name: editUser.name || '',
      });
    }
  }, [isOpen, editUser]);

  useEffect(() => {
    // Valida o formulário sempre que os dados mudarem
    validateForm();
  }, [formData]);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Validação de e-mail
    if (!formData.email) {
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'E-mail inválido';
      isValid = false;
    } else if (
      !isEditMode &&
      existingEmails.includes(formData.email.toLowerCase().trim())
    ) {
      // Apenas valida duplicação no modo de criação
      newErrors.email = 'Este e-mail já está cadastrado nesta empresa';
      isValid = false;
    }

    // Validação de senha (apenas obrigatória no modo de criação)
    if (!isEditMode) {
      if (!formData.password) {
        isValid = false;
      } else if (formData.password.length < 6) {
        newErrors.password = 'A senha deve ter pelo menos 6 caracteres';
        isValid = false;
      }

      // Validação de confirmação de senha
      if (!formData.confirmPassword) {
        isValid = false;
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'As senhas não coincidem';
        isValid = false;
      }
    } else {
      // No modo de edição, senha é opcional
      // Mas se preencher qualquer um dos campos de senha, ambos devem ser preenchidos
      const hasPassword = formData.password && formData.password.trim() !== '';
      const hasConfirmPassword =
        formData.confirmPassword && formData.confirmPassword.trim() !== '';

      if (hasPassword || hasConfirmPassword) {
        // Se preencheu algum campo de senha, ambos são obrigatórios
        if (!hasPassword) {
          newErrors.password = 'Preencha a nova senha';
          isValid = false;
        } else if (formData.password.length < 6) {
          newErrors.password = 'A senha deve ter pelo menos 6 caracteres';
          isValid = false;
        }

        if (!hasConfirmPassword) {
          newErrors.confirmPassword = 'Confirme a nova senha';
          isValid = false;
        } else if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'As senhas não coincidem';
          isValid = false;
        }
      }
    }

    // Validação de cargo
    if (!formData.type) {
      isValid = false;
    }

    // Validação de nome
    if (!formData.name || formData.name.trim() === '') {
      isValid = false;
    }

    setErrors(newErrors);
    setIsFormValid(isValid);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (isFormValid) {
      const dataToSave = {
        email: formData.email.toLowerCase().trim(),
        type: formData.type.value,
        name: formData.name.trim(),
      };

      // Apenas inclui senha se foi preenchida
      if (formData.password) {
        dataToSave.password = formData.password;
      }

      // Se estiver editando, inclui o ID do usuário
      if (isEditMode && editUser) {
        dataToSave.id = editUser.id;
      }

      onSave(dataToSave);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="add-user-modal-overlay"
      onClick={(e) => {
        if (e.target.className === 'add-user-modal-overlay' && !isLoading) {
          onClose();
        }
      }}
    >
      <div className="add-user-modal-container">
        <div className="add-user-modal-header">
          <div className="add-user-modal-header-title">
            <BsPerson className="add-user-modal-header-icon" />
            <h4 className="add-user-modal-title">
              {isEditMode ? 'Editar Usuário' : 'Adicionar Novo Usuário'}
            </h4>
          </div>
          <div className="add-user-modal-header-buttons">
            <DefaultButton
              className="add-user-modal-footer-button"
              label={isLoading ? 'Salvando...' : 'Salvar'}
              icon={<AiOutlineCheck className="button-icon" />}
              onClick={handleSubmit}
              disabled={!isFormValid || isLoading}
            />
            <DefaultButton
              style={{
                backgroundColor: 'transparent',
                border: '1px solid white',
                color: 'white',
              }}
              className="add-user-modal-footer-button cancel"
              label="Cancelar"
              icon={<AiOutlineClose className="button-icon-cancel" />}
              onClick={onClose}
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="add-user-modal-body">
          <div className="add-user-modal-form-section">
            <div className="add-user-modal-form-group">
              <div className="add-user-modal-input-with-icon">
                <BsPerson className="add-user-modal-input-icon" />
                <LabelInput
                  value={formData.name}
                  onChange={(value) => handleChange('name', value)}
                  placeholder="Digite o nome completo"
                  label="Nome*"
                />
              </div>
              {errors.name && (
                <span className="add-user-modal-error-message">
                  {errors.name}
                </span>
              )}
            </div>

            <div className="add-user-modal-form-group">
              <div className="add-user-modal-input-with-icon">
                <BsEnvelope className="add-user-modal-input-icon" />
                <LabelInput
                  value={formData.email}
                  onChange={(value) => handleChange('email', value)}
                  placeholder="usuario@exemplo.com"
                  label="E-mail*"
                  type="email"
                  disabled={isEditMode}
                />
              </div>
              {errors.email && (
                <span className="add-user-modal-error-message">
                  {errors.email}
                </span>
              )}
            </div>

            <div className="add-user-modal-form-group">
              <div className="add-user-modal-input-with-icon">
                <BsKey className="add-user-modal-input-icon" />
                <LabelInput
                  value={formData.password}
                  onChange={(value) => handleChange('password', value)}
                  placeholder={
                    isEditMode
                      ? 'Deixe em branco para não alterar'
                      : 'Mínimo 6 caracteres'
                  }
                  label={isEditMode ? 'Nova Senha (opcional)' : 'Senha*'}
                  type="password"
                />
              </div>
              {errors.password && (
                <span className="add-user-modal-error-message">
                  {errors.password}
                </span>
              )}
            </div>

            <div className="add-user-modal-form-group">
              <div className="add-user-modal-input-with-icon">
                <BsKey className="add-user-modal-input-icon" />
                <LabelInput
                  value={formData.confirmPassword}
                  onChange={(value) => handleChange('confirmPassword', value)}
                  placeholder="Confirme a senha"
                  label={
                    isEditMode ? 'Confirmar Nova Senha' : 'Confirmar Senha*'
                  }
                  type="password"
                />
              </div>
              {errors.confirmPassword && (
                <span className="add-user-modal-error-message">
                  {errors.confirmPassword}
                </span>
              )}
            </div>

            <div className="add-user-modal-form-group">
              <SelectInput
                value={formData.type}
                defaultValue={formData.type}
                onChange={(value) => handleChange('type', value)}
                placeholder="Selecione o cargo"
                label="Cargo*"
                options={USER_TYPES}
                className="add-user-modal-select-input"
              />
              {errors.type && (
                <span className="add-user-modal-error-message">
                  {errors.type}
                </span>
              )}
            </div>
          </div>

          <div className="add-user-modal-form-info">
            <p className="add-user-modal-info-text">
              <strong>Dica:</strong>{' '}
              {isEditMode
                ? 'Você pode alterar o nome e cargo do usuário. Para alterar a senha, preencha os campos de senha.'
                : 'Após o cadastro, o usuário poderá acessar o sistema imediatamente com o e-mail e senha informados.'}
            </p>
            <div className="add-user-modal-cargo-info">
              <p>
                <strong>Cargos disponíveis:</strong>
              </p>
              <ul>
                <li>
                  <strong>Administrador:</strong> Acesso total ao sistema
                </li>
                <li>
                  <strong>Gestor:</strong> Gerencia operações e equipe
                </li>
                <li>
                  <strong>Membro:</strong> Acesso básico para operações
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalAddUser;
